import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSecurityDashboard } from '@/hooks/useSecurityDashboard';
import { useSEO } from '@/hooks/useSEO';
import { SecurityScoreCard } from '@/components/SecurityScoreCard';
import { LoginHistoryTable } from '@/components/LoginHistoryTable';
import LoginLocationMap from '@/components/LoginLocationMap';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Shield, MapPin, Activity, AlertTriangle, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

const SecurityDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { stats, history, loading, refresh } = useSecurityDashboard();

  useSEO({
    title: 'Security Dashboard - ChessHatch',
    description: 'View your personal security statistics, login history, and account safety score',
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth');
    }
  }, [user, navigate]);

  if (!user) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-950/95 to-slate-950">
        <div className="container max-w-7xl mx-auto py-8 px-4 space-y-6">
          <div className="space-y-2">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-5 w-96" />
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-950/95 to-slate-950">
      <div className="container max-w-7xl mx-auto py-8 px-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Shield className="h-8 w-8 text-primary" />
              Security Dashboard
            </h1>
            <p className="text-muted-foreground">
              Monitor your account security and login activity
            </p>
          </div>
          <Button onClick={refresh} variant="outline" size="sm" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {/* Total Logins */}
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Logins</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold">
                  {stats?.total_logins || 0}
                </div>
                <Activity className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                All time login sessions
              </p>
            </CardContent>
          </Card>

          {/* Unique Locations */}
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Unique Locations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-3xl font-bold">
                  {stats?.unique_countries || 0}
                </div>
                <MapPin className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Countries accessed from
              </p>
            </CardContent>
          </Card>

          {/* Suspicious Activity */}
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Suspicious Logins</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className={`text-3xl font-bold ${
                  (stats?.suspicious_logins || 0) > 0 
                    ? 'text-destructive' 
                    : 'text-muted-foreground'
                }`}>
                  {stats?.suspicious_logins || 0}
                </div>
                <AlertTriangle className={`w-8 h-8 ${
                  (stats?.suspicious_logins || 0) > 0 
                    ? 'text-destructive' 
                    : 'text-muted-foreground'
                }`} />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Flagged security events
              </p>
            </CardContent>
          </Card>

          {/* Last Login */}
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Last Login</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <div className="text-lg font-bold">
                  {stats?.last_login 
                    ? format(new Date(stats.last_login), 'MMM d, yyyy')
                    : 'Never'
                  }
                </div>
                <p className="text-xs text-muted-foreground">
                  {stats?.last_login 
                    ? format(new Date(stats.last_login), 'h:mm a')
                    : ''
                  }
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Security Score - Takes 1 column */}
          <div className="lg:col-span-1">
            {stats && (
              <SecurityScoreCard 
                score={stats.security_score} 
                level={stats.security_level}
              />
            )}
          </div>

          {/* Location Analytics - Takes 2 columns */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Login Locations Map
                </CardTitle>
                <CardDescription>
                  Interactive globe showing your recent login locations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Map Component */}
                <LoginLocationMap loginHistory={history} />

                {/* Legend */}
                <div className="flex items-center justify-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-primary border-2 border-white"></div>
                    <span className="text-muted-foreground">Normal Login</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-destructive border-2 border-white"></div>
                    <span className="text-muted-foreground">Suspicious Login</span>
                  </div>
                </div>

                {/* Stats Summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{stats?.unique_countries || 0}</div>
                    <div className="text-sm text-muted-foreground">Countries</div>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{stats?.unique_cities || 0}</div>
                    <div className="text-sm text-muted-foreground">Cities</div>
                  </div>
                </div>

                {/* Info */}
                <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-md">
                  💡 <strong>Privacy Note:</strong> Location data is only collected if you've enabled 
                  location sharing in your security settings. Click markers to view login details.
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Login History - Full Width */}
        <LoginHistoryTable history={history} />

        {/* Security Recommendations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Security Recommendations
            </CardTitle>
            <CardDescription>
              Actions you can take to improve your account security
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">1</span>
                </div>
                <div>
                  <div className="font-medium">Enable Two-Factor Authentication</div>
                  <div className="text-sm text-muted-foreground">
                    Add an extra layer of security with 2FA. Visit{' '}
                    <Button 
                      variant="link" 
                      className="h-auto p-0 text-sm"
                      onClick={() => navigate('/security/settings')}
                    >
                      Security Settings
                    </Button>
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">2</span>
                </div>
                <div>
                  <div className="font-medium">Review Active Sessions</div>
                  <div className="text-sm text-muted-foreground">
                    Check for any unrecognized devices and log them out
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">3</span>
                </div>
                <div>
                  <div className="font-medium">Use a Strong Password</div>
                  <div className="text-sm text-muted-foreground">
                    Use at least 12 characters with a mix of letters, numbers, and symbols
                  </div>
                </div>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SecurityDashboard;
